import '../product-card/product-card-display';
import emit from '../../common/events';
import store from '../../store/store';
import { BaseComponent } from '../base-component/base-component';
import { StoreState } from '../../store/store.interface';
import { booleanConverter } from '../../common/converters';
import { connect } from 'pwa-helpers';
import { customElement, property, state, query } from 'lit/decorators.js';
import { html } from 'lit';
import { performSearch } from './thunks/search.thunk';
import {
  selectSearchPageType,
  selectSelectedCatalog,
  selectSortBy,
  selectAdjustedTotal,
} from './store/search.selector';
import { waitForElementsToLoad } from '../../common/utils/delay-util';
import { TAB_NAMES } from '../tabbed-search/constants';

declare let CustomSelect;
const customSelectSelector = '.customSelect';

/**
 * This object represents one of the options of the sort config selector
 */
export declare interface SortConfig {
  title: string;
  value: string;
}

/**
 * The default configuration of the sort
 */
export const DEFAULT_SORT_CONFIG: Array<SortConfig> = [
  {
    title: 'Relevance',
    value: 'relevance',
  },
  {
    title: 'Title (A-Z)',
    value: 'title_asc',
  },
  {
    title: 'Title (Z-A)',
    value: 'title_desc',
  },
  {
    title: 'Copyright (Newest)',
    value: 'copyright_desc',
  },
  {
    title: 'Copyright (Oldest)',
    value: 'copyright_asc',
  },
];

/**
 * An element to toggle search results view
 *
 * @csspart view - Show search result in list or card view.
 *
 */
@customElement('mhecomm-search-results-sorting')
export class SearchResultsSorting extends connect(store)(BaseComponent) {
  /**
   * This property will decide to update sorting list.
   * Currently this is being used in PLP products tab page.
   */
  @property({ converter: booleanConverter })
  isPlp = false;

  @property()
  categoryId: string | undefined;

  @property()
  layout: string | undefined;

  @property({ converter: booleanConverter })
  tabbedSearchEnabled = false; // Make this reactive

  @state()
  activeTab: string | undefined;

  @state()
  sortBy: string | undefined;

  @state()
  sortList = DEFAULT_SORT_CONFIG;

  @state()
  sortAnnouncement = '';

  @query(customSelectSelector) customSelectEl!: HTMLDivElement;

  @property({ type: Boolean })
  disableSorting = false;

  @property()
  catalog: string | undefined;

  renderComp() {
    return html`
      <span class="visually-hidden" aria-live="polite" aria-atomic="true">${this.sortAnnouncement}</span>
      <div class="d-flex ${this.layout === 'horizontal' ? 'flex-row align-items-center' : 'flex-column'} ">
        <label
          id="sort-by"
          for="results-sort-by"
          class="form-label fw-bold ${this.layout === 'horizontal' ? 'text-nowrap me-2 mb-0' : ''} ${!this.isPlp &&
          this.layout !== 'horizontal'
            ? 'mb-1'
            : ''}"
          >Sort By</label
        >
        <div id="results-sort-by-dropdown" class="results-sort-by-dropdown dropdown customSelect w-100">
          <div
            id="results-sort-by"
            aria-controls="customSelectListBox"
            aria-expanded="false"
            aria-haspopup="listbox"
            aria-labelledby="sort-by"
            data-layer-comp=""
            class="form-select border-secondary mh-emit ${this.disableSorting ? 'disabled' : ''}"
            role="combobox"
            tabindex="0"
            data-custom-select="true">
            <span>${this.sortList[0]?.title}</span>
          </div>
          <input
            id="results-sort-by-input"
            type="hidden"
            name="customSelectInput"
            class="customSelectInput"
            value="${this.sortList[0]?.value}"
            data-automation-id="results-sort-by-input"
            ?disabled="${this.disableSorting}" />
          <ul
            class="dropdown-menu p-0 mt-2 w-100"
            role="listbox"
            id="customSelectListBox"
            aria-labelledby="sort-by"
            tabindex="-1">
            ${this.sortList.map(
              ({ title, value }) =>
                html`<li class="customSelectOption" data-value="${value}">
                  <span class="align-items-center d-flex justify-content-between text-wrap">${title}</span>
                </li>`
            )}
          </ul>
        </div>
      </div>
    `;
  }

  updateSortBy(sortByValue) {
    const sortByVal = sortByValue;
    const state = store.getState() as StoreState; // Get the current state
    const catalog = selectSelectedCatalog(state); // Retrieve catalog using the selector

    emit({
      element: this,
      name: `Sort By`,
      detail: {
        sort: sortByVal,
        searchPageType: selectSearchPageType(state), // Use the existing selector for searchPageType
        'Refinement Type': 'Sort By',
        catalog: catalog, // Add catalog value from the selector
        group: 'relevance',
      },
      dataLayer: true,
    });

    store.dispatch(
      performSearch({
        sortBy: sortByVal ?? undefined,
        start: 1,
        categoryId: this.categoryId,
      })
    );
  }

  /**
   * When this component loads, it needs to identify the is it coming from plp products tab
   * it's own state, and then update the sortlist.
   */
  connectedCallback(): void {
    super.connectedCallback();
    waitForElementsToLoad(this.shadowRoot!, [customSelectSelector]).then(() => {
      if (this.customSelectEl) {
        const customSelectInstance = CustomSelect.getOrCreateInstance(this.customSelectEl);
        this.addTrackedEventListener(this.customSelectEl, 'customSelectChange', (e: Event) => {
          const value = (e as CustomEvent).detail?.value;
          this.sortBy = value;
          this.updateSortBy(this.sortBy);
        });
        this.addTrackedEventListener(
          customSelectInstance.comboEl,
          'keydown',
          (e: Event) => {
            if ((e as KeyboardEvent).key !== 'ArrowDown' || !customSelectInstance.open) return;
            const liveCount = customSelectInstance.listboxEl.querySelectorAll('.customSelectOption').length;
            if (customSelectInstance.activeIndex >= liveCount - 1) {
              e.preventDefault();
              e.stopImmediatePropagation();
              customSelectInstance.onOptionChange(0);
            }
          },
          { capture: true }
        );
      }
    });
    if (this.isPlp) {
      this.sortList = this._plpProductSortList();
    }
    if (this.tabbedSearchEnabled) {
      this.sortList = this._tabPageSortList();
    }
  }

  protected updated(_changedProperties: Map<string | boolean | number | symbol, unknown>): void {
    if (_changedProperties.has('disableSorting')) {
      if (this.customSelectEl) {
        const customSelectInstance = CustomSelect.getOrCreateInstance(this.customSelectEl);
        customSelectInstance.setDisabled(this.disableSorting);
        this.sortBy = this.sortList[0].value;
        customSelectInstance.setValue(this.sortBy, { dispatch: false });
      }
    }
    if (_changedProperties.has('catalog') && this.tabbedSearchEnabled) {
      this.sortList = this._tabPageSortList();
      if (this.customSelectEl) {
        const customSelectInstance = CustomSelect.getOrCreateInstance(this.customSelectEl);
        this.sortBy = this.sortList[0].value;
        customSelectInstance.setValue(this.sortBy, { dispatch: false });
      }
    }
    // Sync CustomSelect visible label/input when `sortBy` is hydrated from the store
    // (e.g. cold load with `?sortby=title_asc` in the URL). The template renders
    // `sortList[0]` by default, so without this the dropdown stays on "Relevance"
    // even when the Redux request already has the correct sort applied.
    // Guarded to skip when catalog/disableSorting already handled the sync above.
    if (
      _changedProperties.has('sortBy') &&
      !_changedProperties.has('catalog') &&
      !_changedProperties.has('disableSorting') &&
      this.sortBy &&
      this.customSelectEl
    ) {
      const customSelectInstance = CustomSelect.getOrCreateInstance(this.customSelectEl);
      customSelectInstance.setValue(this.sortBy, { dispatch: false });
    }
  }

  stateChanged(state: StoreState) {
    this.sortBy = selectSortBy(state);
    const total = selectAdjustedTotal(state);
    const sortTitle = this.sortList.find((item) => item.value === this.sortBy)?.title || '';

    if (sortTitle && total !== undefined) {
      // Delay the announcement to allow the dropdown close action to complete first
      setTimeout(() => {
        this.sortAnnouncement = `Products sorted by ${sortTitle}. ${total} ${
          total === 1 ? 'result' : 'results'
        } displayed.`;
      }, 500);
    }

    if (this.tabbedSearchEnabled) {
      this.disableSorting = false;
      this.activeTab = state.tabbedSearchActiveTabState.activeTabKey;
      if (this.activeTab !== TAB_NAMES.PRODUCT) {
        this.sortBy = DEFAULT_SORT_CONFIG[0].value;
        this.disableSorting = true;
      }
      if (this.tabbedSearchEnabled) {
        this.catalog = selectSelectedCatalog(state);
      }
    }
  }

  private _plpProductSortList() {
    return [
      {
        title: 'Copyright: Newest First',
        value: 'plp_copyright_desc',
      },
      {
        title: 'Copyright: Oldest First',
        value: 'plp_copyright_asc',
      },
      {
        title: 'Price: High to Low',
        value: 'price_desc',
      },
      {
        title: 'Price: Low to High',
        value: 'price_asc',
      },
      {
        title: 'Title: A to Z',
        value: 'title_asc',
      },
      {
        title: 'Title: Z to A',
        value: 'title_desc',
      },
    ];
  }

  private _tabPageSortList() {
    const state = store.getState() as StoreState;
    const catalog = selectSelectedCatalog(state);

    // Remove copyright sort options if catalog is 'HE'
    const sortList = [
      {
        title: 'Relevance',
        value: 'relevance',
      },
      {
        title: 'Copyright (Newest - Oldest)',
        value: 'copyright_desc',
      },
      {
        title: 'Copyright (Oldest - Newest)',
        value: 'copyright_asc',
      },
      {
        title: 'Title (A-Z)',
        value: 'title_asc',
      },
      {
        title: 'Title (Z-A)',
        value: 'title_desc',
      },
    ];
    if (catalog?.toUpperCase() === 'HE') {
      // Filter out copyright options
      return sortList.filter((item) => item.value !== 'copyright_desc' && item.value !== 'copyright_asc');
    }
    return sortList;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-search-results-sorting': SearchResultsSorting;
  }
}
